import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, map, take, tap, throwError } from 'rxjs';

import { PreferencesResponse, TenantAdminApiService } from './tenant-admin-api.service';

/**
 * PreferencesService — the single client-side owner of the operator's
 * server-persisted preference map (Story 29.2 rework).
 *
 * The map lives on the User entity (`User.Preferences`, DynamoDB Map of
 * String) and is the ONLY persistence layer — deliberately no localStorage:
 * preferences follow the operator across browsers and devices, and signing in
 * on a fresh machine restores them. Consumers (ThemeService,
 * ViewPreferenceService) read through the in-memory cache and subscribe to
 * `changes$` for the post-sign-in load and cross-consumer updates.
 *
 * Lifecycle: `load()` fires once per session, normally from the sidebar shell
 * (the first authenticated surface, via `ThemeService.loadFromBackend()`), and
 * is safe to call again later (e.g. a fresh `ngOnInit` after a same-tab
 * logout/re-login as a different operator — this is a `providedIn: 'root'`
 * singleton, so nothing else resets its state). Until the first load lands,
 * `get()` returns undefined and consumers fall back to their defaults.
 *
 * `ready$` is self-sufficient: it triggers the FIRST `load()` itself if
 * nothing has requested one yet, so a consumer never depends on some OTHER
 * caller having run `load()` first, or on being ordered ahead of one that
 * does (2026-09-07 review — `checkMfaNudge()` was the first consumer whose
 * correctness actually depended on that ordering, and it wasn't enforced
 * anywhere). It does NOT force a reload once one has ever happened — repeat
 * subscriptions just wait on the current `loaded$` state, same as before.
 * `load()` itself resets `loaded$` to false at the start of every fetch (not
 * just the first), so a `ready$` subscriber arriving mid-reload correctly
 * waits for the fresh result instead of resolving instantly with the stale
 * pre-reload snapshot.
 */
@Injectable({ providedIn: 'root' })
export class PreferencesService {
  private readonly api = inject(TenantAdminApiService);
  private readonly prefs$ = new BehaviorSubject<Record<string, string>>({});

  /** The current preference map; emits {} pre-load, then on every change. */
  readonly changes$: Observable<Record<string, string>> = this.prefs$.asObservable();

  private readonly loaded$ = new BehaviorSubject(false);
  private loading = false;
  private everLoaded = false;

  /**
   * Emits the map ONCE the current load has settled (success or failure — a
   * failed load settles as {} so consumers keep their defaults) and
   * completes. Story 47.3: the shell's MFA nudge needs the snooze key AFTER
   * the load, not the pre-load {} that `changes$` starts with.
   */
  readonly ready$: Observable<Record<string, string>> = this.loaded$.pipe(
    tap(() => this.ensureLoaded()),
    filter(Boolean),
    take(1),
    map(() => this.prefs$.value),
  );

  /** Kicks off the FIRST load if nothing has requested one yet; otherwise a no-op. */
  private ensureLoaded(): void {
    if (!this.everLoaded && !this.loading) this.load();
  }

  /**
   * Fetches the operator's map from the backend. Guards only against
   * CONCURRENT duplicate fetches (`loading`) — calling `load()` again after a
   * previous one has already landed starts a genuine new fetch, which is the
   * intended way to refresh the map (e.g. after a same-tab re-login). Silent
   * on error — preferences are non-critical; consumers keep their defaults.
   */
  load(): void {
    if (this.loading) return;
    this.loading = true;
    this.everLoaded = true;
    this.loaded$.next(false);
    this.api.fetchPreferences().subscribe({
      next: (res) => { this.prefs$.next(res.preferences ?? {}); this.loading = false; this.loaded$.next(true); },
      error: () => { this.loading = false; this.loaded$.next(true); },
    });
  }

  /** Cached read; undefined when the key is unset or the map hasn't loaded. */
  get(key: string): string | undefined {
    return this.prefs$.value[key];
  }

  /**
   * Optimistically applies {key: value} to the cache, then PATCHes the merge.
   * On success the cache is replaced with the server's authoritative map; on
   * error the cache reverts and the error is rethrown so the caller can react
   * (snackbar, revert UI state — consumers of `changes$` revert automatically).
   */
  set(key: string, value: string): Observable<PreferencesResponse> {
    const previous = this.prefs$.value;
    this.prefs$.next({ ...previous, [key]: value });
    return this.api.patchPreferences({ preferences: { [key]: value } }).pipe(
      tap((res) => this.prefs$.next(res.preferences ?? {})),
      catchError((err) => {
        this.prefs$.next(previous);
        return throwError(() => err);
      }),
    );
  }
}
